import { nanoid } from "nanoid";
import { useCallback } from "react";
import type { FpUserMessageCreate } from "../../../schemas/codegen";
import {
  useAttachmentStore,
  useInputStore,
  useMessageStore,
} from "../../state/stores";
import { projectWebSocketManager } from "../websocket";
import { useActiveProjectId } from "./useActiveProjectId";

export function useSendProjectMessage() {
  const activeProjectId = useActiveProjectId();

  // Get actions from stores (zustand functions are stable by default)
  const addMessage = useMessageStore((state) => state.addMessage);
  const clearInput = useInputStore((state) => state.clearInput);
  const attachmentFiles = useAttachmentStore((state) => state.files);
  const clearAttachments = useAttachmentStore((state) => state.clearAll);

  const sendMessage = useCallback(
    (content: string) => {
      if (!activeProjectId) {
        console.error("No active project ID for sending message");
        return;
      }

      const messageId = nanoid();
      const messageData: FpUserMessageCreate = {
        type: "user.message.create",
        messageId,
        content,
        // Include attachments if any exist
        attachments: attachmentFiles.length > 0 ? attachmentFiles : undefined,
      };

      // Add message to state immediately
      addMessage({
        id: messageId,
        sender: "user",
        content,
        status: "completed",
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      // Clear input and attachments after message is added to state
      clearInput();
      clearAttachments();

      // Send via the singleton manager
      const connection = projectWebSocketManager.getConnection(activeProjectId);
      connection.send(messageData);
    },
    [
      activeProjectId,
      addMessage,
      attachmentFiles,
      clearAttachments,
      clearInput,
    ],
  );

  return sendMessage;
}
